<!-- Responsibility: Orchestrate the active play scene shell and scene-level interactions.
Scope: Compose scene panels/overlays and dispatch scene UI events; no modal shell logic. -->
<scene-view>
  <main class="adventure-shell { state.historyOpen ? 'leftbar-open' : '' } { state.statsOpen ? 'rightbar-open' : '' }">
    <button
      class="history-drawer-backdrop"
      aria-label="Close field log drawer"
      aria-hidden={ state.historyOpen ? 'false' : 'true' }
      tabindex={ state.historyOpen ? '0' : '-1' }
      onclick={ closeHistory }
    ></button>

    <scene-history-sidebar
      view={ historySidebarView() }
      actions={ historySidebarActions() }
    ></scene-history-sidebar>

    <!-- Center: current scene -->
    <article class="scene-center">
      <div class="scene-toolbar">
        <button class="hist-btn" data-e2e-open="history" onclick={ openHistory } onfocus={ collapseSceneChrome }>◀ Log</button>
        <span class="toolbar-room">{ state.roomName }</span>
        <button class="help-btn" data-e2e-open="help" onclick={ toggleHints } onfocus={ collapseSceneChrome } data-tip="How to play" aria-label="How to play">?</button>
        <button class="notes-btn" data-e2e-open="notebook" onclick={ openNotebook } onfocus={ collapseSceneChrome } aria-label="Open notebook">Notes { state.notebookCount }</button>
        <button class="stats-btn" data-e2e-open="world" onclick={ openStats } onfocus={ collapseSceneChrome }>World ▶</button>
      </div>

      <div class="scene-content { isSceneSparse() ? 'sparse' : '' }">
        <div if={ isSceneBootLoading() } class="scene-boot-card scene-boot-loading" aria-live="polite">
          <strong>Recovering reality...</strong>
          <span>Restoring the active save and scene state.</span>
        </div>

        <div if={ hasSceneBootError() } class="scene-boot-card scene-boot-error" role="alert">
          <strong>Scene recovery failed</strong>
          <span>{ state.sceneBootError }</span>
          <div class="scene-boot-actions">
            <button onclick={ retrySceneLoad }>Retry restore</button>
            <button onclick={ returnToHub }>Return to hub</button>
          </div>
        </div>

        <div if={ !isSceneBootBlocked() && !state.hasVision && visionHint() } class="scene-vision-hint">{ visionHint() }</div>

        <section class="scene-focus-stack" if={ !isSceneBootBlocked() }>
          <div class="scene-focus-main">
            <scene-current-location
              view={ currentLocationView() }
              actions={ currentLocationActions() }
            ></scene-current-location>

            <scene-journal visible={ !isSceneBootBlocked() } entries={ getRecentEntriesView() }></scene-journal>
          </div>

          <scene-action-rail
            view={ actionRailView() }
            actions={ actionRailActions() }
          ></scene-action-rail>
        </section>
      </div>
    </article>

    <scene-world-sidebar
      activeTab={ state.activeTab }
      isOpen={ state.statsOpen }
      closeStats={ closeStats }
      switchTab={ switchTab }
    ></scene-world-sidebar>

  </main>

  <combat-modal></combat-modal>
  <event-modal></event-modal>
  <tutorial-overlay></tutorial-overlay>

  <div id="vision-lightbox" class="vision-lightbox { state.lightboxOpen ? 'is-open' : '' } { state.lightboxZoomed ? 'zoomed' : '' }" hidden={ state.lightboxOpen ? null : '' } onclick={ closeLightbox }>
    <img
      class="vl-img"
      src={ state.visionUrl }
      alt="Scene vision"
      tabindex="0"
      onclick={ toggleLightboxZoom }
      onkeydown={ (e) => onActivateKey(e, toggleLightboxZoom) }
    >
    <button class="vl-close modal-close-hit" aria-label="Close vision lightbox" onclick={ closeLightbox }>✕</button>
  </div>

  <script>
    import { dispatcher, navigateState, stateService, sysNotify } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { authStore } from '../stores/auth-store.js'
    import * as gameStore from '../../game-store.js'
    import { formatAIErrorMessage } from '../../ai/errors.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { loadAIConfig } from '../../ai/config.js'
    import { getCompanionActions } from '../../engine/permissions.js'
    import { buildActionTarget, buildToolRef } from '../helpers/action-refs.ts'
    import { buildCompanionDialogOptions } from '../helpers/companion-modal.js'
    import { buildAbilityModalActions } from '../helpers/ability-actions.ts'
    import { buildInfoDialog, openItemDialog } from '../dialog/dialog-openers.ts'
    import { effectChipText as formatEffectChipText } from '../helpers/effect-chip.js'
    import { getActiveSceneEntries, getActiveSceneTailCount as countActiveSceneTail } from '../helpers/history-window.js'
    import { NotebookModel } from '../notebook/notebook-model.ts'
    import { buildWorldLogModalBody, getWorldLogPreview, getWorldLogTypeLabel } from '../helpers/world-log.js'
    import { PROCESS_LINES, DEFAULT_PROCESS_LINE } from '../helpers/action-processing.ts'
    import { formatVitalValue } from '../../engine/vitals'
    import { getAppImportMetaEnv } from "@anyquest/shared-core"
    import TutorialOverlay      from '../components/tutorial-overlay.riot'
    import CombatModal          from '../components/combat-modal.riot'
    import EventModal           from '../components/event-modal.riot'
    import AtlasMap             from '../components/atlas-map.riot'
    import SceneActionRail      from '../components/scene-action-rail.riot'
    import SceneCurrentLocation from '../components/scene-current-location.riot'
    import SceneHistorySidebar  from '../components/scene-history-sidebar.riot'
    import SceneJournal         from '../components/scene-journal.riot'
    import SceneWorldSidebar    from '../components/scene-world-sidebar.riot'

    const appEnv = getAppImportMetaEnv()

    export default {
      components: {
        TutorialOverlay, CombatModal, EventModal, AtlasMap, SceneActionRail, SceneCurrentLocation, SceneHistorySidebar, SceneJournal, SceneWorldSidebar,
      },

      // Expose the live store reference so templates can read it directly
      // (same pattern as journal-feed — avoids Riot dirty-check false negatives)
      gameStore,

      state: {
        activeTab:    'atlas',
        historyOpen:  false,
        statsOpen:    false,
        locationExpanded: false,
        hudExpanded: false,
        visionUrl:    '',
        portraitUrl:  '',
        hasVision:    false,
        lightboxOpen: false,
        lightboxZoomed: false,
        roomName:     'VOID',
        currentRoom:  null,
        currentRoomDesc: '',
        companions:   [],
        roomNpcs:     [],
        roomItems:    [],
        roomFlags:    [],
        roomDormantFlags: [],
        playerVitals: null,
        playerName:   '',
        playerBio:    '',
        playerEffects: [],
        playerDormantEffects: [],
        worldLog: [],
        quickAbilities: [],
        quickExitActions: [],
        actionProcessing: false,
        actionProcessingText: DEFAULT_PROCESS_LINE,
        activeActionId: null,
        notebookCount: 0,
        selectedCompanionName: '',
        unreadEntryKey: '',
        sceneBootStatus: 'idle',
        sceneBootError: '',
      },

      // Keep the active scene in center view until location context changes.
      getRecentEntries() {
        const h = (gameStore.state || {}).history || []
        return getActiveSceneEntries(h, 6)
      },

      getRecentEntriesDecorated() {
        const entries = this.getRecentEntries()
        if (!entries.length) return []
        const h = (gameStore.state || {}).history || []
        const startIndex = Math.max(0, h.length - entries.length)
        const keyed = entries.map((entry, idx) => {
          const historyIndex = startIndex + idx
          return {
            ...entry,
            historyIndex,
            entryKey: this.historyEntryKey(entry, historyIndex),
          }
        })
        return this.decorateTemporalEntryRuns(keyed, 3)
      },

      getRecentEntriesView() {
        return this.getRecentEntriesDecorated().map(entry => ({
          ...entry,
          isUnread: this.isEntryUnread(entry),
        }))
      },

      getActiveSceneTailCount() {
        const h = (gameStore.state || {}).history || []
        return countActiveSceneTail(h, 6)
      },

      historyEntryKey(entry, historyIndex = 0) {
        const location = String(entry?.location ?? '').trim().toLowerCase()
        const time = String(entry?.time ?? '').trim()
        const text = String(entry?.text ?? '').replace(/\s+/g, ' ').trim().slice(0, 120)
        return `${location}::${time}::${historyIndex}::${text}`
      },

      decorateTemporalEntryRuns(entries, minRun = 3) {
        const list = Array.isArray(entries) ? entries : []
        if (!list.length) return []
        const out = []
        let i = 0
        while (i < list.length) {
          const location = String(list[i]?.location ?? '').trim().toLowerCase()
          let j = i + 1
          while (j < list.length) {
            const nextLocation = String(list[j]?.location ?? '').trim().toLowerCase()
            if (nextLocation !== location) break
            j += 1
          }
          const runLength = j - i
          for (let k = i; k < j; k += 1) {
            const entry = { ...list[k], temporalDivider: false, temporalLabel: '' }
            if (k === i && runLength >= minRun) {
              const room = String(list[i]?.location ?? 'Unknown').trim() || 'Unknown'
              const fromTime = String(list[i]?.time ?? '').trim()
              const toTime = String(list[j - 1]?.time ?? '').trim()
              const hasTimeRange = !!fromTime && !!toTime && fromTime !== toTime
              const range = hasTimeRange ? `${fromTime} → ${toTime}` : (fromTime || toTime || '')
              entry.temporalDivider = true
              entry.temporalLabel = range
                ? `${room} · ${runLength} moments · ${range}`
                : `${room} · ${runLength} moments`
            }
            out.push(entry)
          }
          i = j
        }
        return out
      },

      markLatestEntryUnread() {
        const h = (gameStore.state || {}).history || []
        if (!h.length) {
          if (this.state.unreadEntryKey) this.update({ unreadEntryKey: '' })
          return
        }
        const index = h.length - 1
        this.update({ unreadEntryKey: this.historyEntryKey(h[index], index) })
      },

      clearUnreadLatestEntry() {
        if (!this.state.unreadEntryKey) return
        this.update({ unreadEntryKey: '' })
      },

      isEntryUnread(entry) {
        if (!entry?.isLatest) return false
        const key = entry.entryKey || this.historyEntryKey(entry, entry.historyIndex ?? 0)
        return key === this.state.unreadEntryKey
      },

      isSceneSparse() {
        const entries = this.getRecentEntries().filter(entry => String(entry?.text ?? '').trim().length > 0)
        if (entries.length > 1) return false
        const contextCount = (this.state.companions?.length ?? 0) + (this.state.roomNpcs?.length ?? 0) + (this.state.roomItems?.length ?? 0)
        const roomSignals = (this.state.roomFlags?.length ?? 0) + (this.state.roomDormantFlags?.length ?? 0)
        return contextCount <= 2 && roomSignals <= 1
      },

      isSceneBootLoading() {
        return this.state.sceneBootStatus === 'loading' && !this.hasRenderableSceneState()
      },

      hasSceneBootError() {
        return this.state.sceneBootStatus === 'error' && !this.hasRenderableSceneState()
      },

      isSceneBootBlocked() {
        return this.isSceneBootLoading() || this.hasSceneBootError()
      },

      isSceneInteractive() {
        return !this.isSceneBootBlocked() && !!gameStore.state
      },

      hasRenderableSceneState() {
        return !!this.state.currentRoom || this.getRecentEntries().length > 0
      },

      shouldShowActionRail() {
        return !!(
          this.state.playerVitals
          || this.state.quickExitActions.length
          || this.state.quickAbilities.length
        )
      },

      shouldShowQuickTop() {
        return !!(
          this.state.quickExitActions.length
          || this.state.quickAbilities.length
        )
      },

      actionRailView() {
        const gs = gameStore.state
        const quickAbilities = gs ? this.collectQuickAbilities(gs) : this.state.quickAbilities
        const quickExitActions = gs ? this.collectExitActions(gs) : this.state.quickExitActions
        return {
          showHud: this.shouldShowActionRail(),
          expanded: this.state.hudExpanded,
          playerVitals: this.state.playerVitals,
          quickAbilities,
          quickExitActions,
          showQuickTop: !!(quickExitActions.length || quickAbilities.length),
          interactive: this.isSceneInteractive(),
          processing: this.state.actionProcessing,
          processingText: this.state.actionProcessingText,
        }
      },

      actionRailActions() {
        return {
          expandHud: () => this.expandHud(),
          collapseHud: () => this.collapseHud(),
          openVitalModal: (kind) => this.openVitalModal(kind),
          onActivateKey: (event, callback) => this.onActivateKey(event, callback),
          openAbilityModal: (ability) => this.openAbilityModal(ability),
          abilityHoverText: (ability) => this.abilityHoverText(ability),
          useExitAction: (exitAction) => this.useExitAction(exitAction),
        }
      },

      historySidebarView() {
        return {
          portraitUrl: this.state.portraitUrl,
          playerName: this.state.playerName,
          companions: this.state.companions,
          playerVitals: this.state.playerVitals,
          playerEffectChips: this.state.playerEffects.map(fx => this.effectChipText(fx)),
          playerDormantEffectChips: this.state.playerDormantEffects.map(fx => this.effectChipText(fx, true)),
          worldLog: this.state.worldLog,
          excludeTailCount: this.isNarrowHistoryDrawer() ? 0 : this.getActiveSceneTailCount(),
        }
      },

      historySidebarActions() {
        return {
          closeHistory: () => this.closeHistory(),
          onActivateKey: (event, callback) => this.onActivateKey(event, callback),
          openPlayerModal: () => this.openPlayerModal(),
          openCompanionModal: (companion) => this.openCompanionModal(companion),
          openVitalModal: (kind) => this.openVitalModal(kind),
          openWorldLogModal: () => this.openWorldLogModal(),
          openWorldLogEntry: (entry) => this.openWorldLogEntry(entry),
        }
      },

      currentLocationView() {
        return {
          visible: !this.isSceneBootBlocked() && this.shouldShowCurrentRoomDescription(),
          expanded: true,
          interactive: false,
          redundant: false,
          roomName: String(this.state.currentRoom?.name ?? '').trim() || 'Unknown location',
          npcCount: this.state.roomNpcs.length,
          itemCount: this.state.roomItems.length,
          hasVision: this.state.hasVision,
          visionUrl: this.state.visionUrl,
          currentRoomDesc: this.state.currentRoomDesc,
          hasPresence: !!(this.state.currentRoom && (this.state.companions.length || this.state.roomNpcs.length || this.state.roomItems.length || this.state.roomFlags.length || this.state.roomDormantFlags.length)),
          companions: this.state.companions.map(companion => ({
            ...companion,
            isSelected: this.state.selectedCompanionName === companion.name,
            activeEffectCount: this.companionActiveEffectCount(companion),
            ariaLabel: this.companionChipAriaLabel(companion),
          })),
          roomNpcs: this.state.roomNpcs,
          roomItems: this.state.roomItems,
          roomFlags: this.state.roomFlags,
          roomDormantFlags: this.state.roomDormantFlags,
          showRoomLegend: this.showRoomLegend(),
          roomLegendItems: this.roomLegendItems(),
        }
      },

      currentLocationActions() {
        return {
          focusDetails: () => this.focusRoomDetails(),
          expandDetails: () => this.expandLocationCard(),
          collapseDetails: () => this.collapseLocationCard(),
          onActivateKey: (event, callback) => this.onActivateKey(event, callback),
          openCompanionFromChip: (companion) => this.openCompanionFromChip(companion),
          openVisionLightbox: () => this.openVisionLightbox(),
        }
      },

      shouldShowCurrentRoomDescription() {
        if (this.state.hasVision) return true
        const roomDesc = String(this.state.currentRoomDesc ?? '').trim()
        if (!roomDesc) return !!(this.state.currentRoom && (this.state.companions.length || this.state.roomNpcs.length || this.state.roomItems.length || this.state.roomFlags.length || this.state.roomDormantFlags.length))
        const latest = this.getRecentEntries().find(entry => entry.isLatest)
        const latestText = String(latest?.text ?? '').trim()
        if (!latestText) return true
        if (roomDesc.toLowerCase() !== latestText.toLowerCase()) return true
        return !!(this.state.currentRoom && (this.state.companions.length || this.state.roomNpcs.length || this.state.roomItems.length || this.state.roomFlags.length || this.state.roomDormantFlags.length))
      },

      roomLegendItems() {
        const chips = []
        if ((this.state.companions?.length ?? 0) > 0) chips.push({ kind: 'ally', label: 'Ally' })
        if ((this.state.roomNpcs?.length ?? 0) > 0) chips.push({ kind: 'npc', label: 'NPC' })
        if ((this.state.roomItems?.length ?? 0) > 0) chips.push({ kind: 'item', label: 'Item' })
        if ((this.state.roomFlags?.length ?? 0) > 0 || (this.state.roomDormantFlags?.length ?? 0) > 0) {
          chips.push({ kind: 'cond', label: 'Condition' })
        }
        return chips
      },

      showRoomLegend() {
        return this.roomLegendItems().length > 1
      },

      visionHint() {
        if (this.state.hasVision) return ''
        const cfg = loadAIConfig()
        const hasVisualProvider = !!(
          String(cfg.geminiApiKey ?? '').trim()
          || String(cfg.geminiProxyUrl ?? '').trim()
          || String(appEnv.VITE_GEMINI_API_KEY ?? '').trim()
          || String(appEnv.VITE_GEMINI_PROXY_URL ?? '').trim()
        )
        return hasVisualProvider
          ? 'Generating scene visual...'
          : 'No image provider configured. Add Gemini key/proxy in AI settings for visuals.'
      },

      formatFlags(flags) {
        if (!flags) return []
        return Object.entries(flags)
          .filter(([_, v]) => v !== false && v !== null) // Hide false/null flags entirely
          .map(([k, v]) => {
            const prettyKey = k.replace(/_/g, ' ').toUpperCase()
            return v === true ? prettyKey : `${prettyKey}: ${v}`
          })
      },

      collectPlayerEffects(gs) {
        const nowMin = (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
        const effects = []
        for (const mods of Object.values(gs.player.modifiers ?? {})) {
          for (const m of mods ?? []) {
            if (m.expiresAt != null && m.expiresAt <= nowMin) continue
            effects.push(m.label || m.source || 'Effect')
          }
        }
        return [...new Set(effects)].slice(0, 8)
      },

      collectDormantEffects(gs) {
        return [...new Set((gs.player.dormantEffects ?? []).map(e => e.label || e.source || 'Effect'))].slice(0, 6)
      },

      collectEntityEffects(entity, nowMin) {
        const effects = []
        for (const mods of Object.values(entity?.modifiers ?? {})) {
          for (const m of mods ?? []) {
            if (m.expiresAt != null && m.expiresAt <= nowMin) continue
            effects.push(m.label || m.source || 'Effect')
          }
        }
        return [...new Set(effects)]
      },

      collectEntityDormantEffects(entity) {
        return [...new Set((entity?.dormantEffects ?? []).map(e => e.label || e.source || 'Effect'))]
      },

      collectRoomDormantFlags(gs) {
        return [...new Set((gs.currentRoom?.dormantFlags ?? []).map(f => {
          if (typeof f === 'string') return f
          return f?.label || f?.key || 'Condition'
        }))].slice(0, 6)
      },

      collectQuickAbilities(gs) {
        const totalMin = (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
        return (gs.player.abilities ?? []).slice(0, 8).map(ab => {
          const lastUsed = ab.lastUsed ?? -Infinity
          const cooldownRemaining = Math.max(0, (lastUsed + ab.cooldown) - totalMin)
          return {
            ...ab,
            onCooldown: cooldownRemaining > 0,
            cooldownRemaining,
          }
        })
      },

      collectRoomExits(gs) {
        const edges = gs?.atlas?.edges ?? []
        const nodes = gs?.atlas?.nodes ?? {}
        const x = gs?.player?.location?.x ?? 0
        const y = gs?.player?.location?.y ?? 0
        const byDirection = new Map()
        const directionRank = { north: 0, east: 1, south: 2, west: 3 }

        for (const edge of edges) {
          let nx = null
          let ny = null
          if (edge.x1 === x && edge.y1 === y) {
            nx = edge.x2
            ny = edge.y2
          } else if (edge.x2 === x && edge.y2 === y) {
            nx = edge.x1
            ny = edge.y1
          }
          if (nx == null || ny == null) continue

          const dx = nx - x
          const dy = ny - y
          let direction = ''
          if (dx === 0 && dy === 1) direction = 'north'
          else if (dx === 1 && dy === 0) direction = 'east'
          else if (dx === 0 && dy === -1) direction = 'south'
          else if (dx === -1 && dy === 0) direction = 'west'
          if (!direction) continue

          const key = `${nx}_${ny}`
          const target = nodes[key]
          byDirection.set(direction, {
            direction,
            roomName: target?.name ?? `Unknown ${direction}`,
            key,
            rank: directionRank[direction] ?? 99,
          })
        }

        return [...byDirection.values()].sort((a, b) => a.rank - b.rank)
      },

      collectExitActions(gs) {
        const exits = this.collectRoomExits(gs)
        if (exits.length) {
          return exits.map(exit => ({
            label: `Go ${exit.direction[0].toUpperCase()}${exit.direction.slice(1)}`,
            input: `Go ${exit.direction}`,
            roomName: exit.roomName,
            style: 'go',
            hint: `Move toward ${exit.roomName}`,
          }))
        }

        const fallbackDirections = ['north', 'east', 'south', 'west']
        return fallbackDirections.map(direction => ({
          label: `Explore ${direction[0].toUpperCase()}${direction.slice(1)}`,
          input: `Go ${direction}`,
          roomName: `Unknown ${direction}`,
          style: 'explore',
          hint: `Explore an unknown route toward ${direction}`,
        }))
      },

      collectWorldLog(gs) {
        return getWorldLogPreview(gs.worldLog ?? [])
      },

      companionActiveEffectCount(companion) {
        const gs = gameStore.state
        const nowMin = gs
          ? (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
          : 0
        return this.collectEntityEffects(companion, nowMin).length
      },

      companionChipAriaLabel(companion) {
        const name = String(companion?.name ?? 'Companion').trim() || 'Companion'
        const vitality = formatVitalValue(companion?.vitals ?? {}, 'vitality')
        const activeFx = this.companionActiveEffectCount(companion)
        return `Inspect companion ${name}. Vitality ${vitality}. Active effects ${activeFx}. Action opens companion details and available actions.`
      },

      markCompanionSelected(companionName) {
        const name = String(companionName ?? '').trim()
        if (!name) return
        if (this.companionSelectedTimer) clearTimeout(this.companionSelectedTimer)
        this.update({ selectedCompanionName: name })
        this.companionSelectedTimer = setTimeout(() => {
          this.update({ selectedCompanionName: '' })
        }, 2400)
      },

      clearCompanionSelected() {
        if (this.companionSelectedTimer) clearTimeout(this.companionSelectedTimer)
        this.update({ selectedCompanionName: '' })
      },

      syncStateFromGame(gs) {
        if (!gs) return
        this.update({
          roomName:     gs.currentRoom.name,
          currentRoomDesc: String(gs.currentRoom?.desc ?? '').trim(),
          visionUrl:    gs.visionUrl   ?? '',
          portraitUrl:  gs.portraitUrl ?? '',
          hasVision:    !!gs.visionUrl,
          currentRoom:  gs.currentRoom,
          companions:   gs.companions ?? [],
          roomNpcs:     gs.currentRoom?.npcs  ?? [],
          roomItems:    gs.currentRoom?.items ?? [],
          roomFlags:    this.formatFlags(gs.currentRoom?.flags),
          roomDormantFlags: this.collectRoomDormantFlags(gs),
          playerVitals: gs.player?.vitals ?? null,
          playerName:   gs.player?.name || (gs.player?.identity ?? '').split(/[\s,]+/).slice(0, 2).join(' ') || '',
          playerBio:    gs.player?.bio ?? '',
          playerEffects: this.collectPlayerEffects(gs),
          playerDormantEffects: this.collectDormantEffects(gs),
          worldLog:     this.collectWorldLog(gs),
          quickAbilities: this.collectQuickAbilities(gs),
          quickExitActions: this.collectExitActions(gs),
          notebookCount: Array.isArray(gs.notebook) ? gs.notebook.length : 0,
          selectedCompanionName: '',
          unreadEntryKey: '',
        })
      },

      pushSceneOverlayEntry(key) {
        const overlayKey = String(key ?? '').trim()
        if (!overlayKey) return
        if (!Array.isArray(this.sceneOverlayStack)) this.sceneOverlayStack = []
        const baseState = history.state && typeof history.state === 'object' ? history.state : {}
        try {
          history.pushState({
            ...baseState,
            am2SceneOverlay: true,
            am2SceneOverlayKey: overlayKey,
            am2SceneOverlayTs: Date.now(),
          }, '')
          this.sceneOverlayStack.push(overlayKey)
        } catch {
          // ignore history push failures
        }
      },

      consumeSceneOverlayEntry(key) {
        const overlayKey = String(key ?? '').trim()
        if (!overlayKey || !Array.isArray(this.sceneOverlayStack) || !this.sceneOverlayStack.length) return
        const top = this.sceneOverlayStack[this.sceneOverlayStack.length - 1]
        if (top !== overlayKey) {
          const idx = this.sceneOverlayStack.lastIndexOf(overlayKey)
          if (idx >= 0) this.sceneOverlayStack.splice(idx, 1)
          return
        }
        const currentState = history.state && typeof history.state === 'object' ? history.state : null
        if (currentState?.am2SceneOverlay === true && currentState?.am2SceneOverlayKey === overlayKey) {
          this.ignoreSceneOverlayPop = true
          history.back()
          return
        }
        this.sceneOverlayStack.pop()
      },

      closeSceneOverlayFromPop(key) {
        if (key === 'lightbox') {
          if (this.state.lightboxOpen) this.closeLightbox({ fromPop: true })
        }
      },

      handleSceneOverlayPopState(event) {
        if (this.ignoreSceneOverlayPop) {
          this.ignoreSceneOverlayPop = false
          if (Array.isArray(this.sceneOverlayStack) && this.sceneOverlayStack.length) this.sceneOverlayStack.pop()
          return
        }
        if (document.querySelector('dialog[open]')) return
        const stack = Array.isArray(this.sceneOverlayStack) ? this.sceneOverlayStack : []
        if (!stack.length) return
        const currentState = event?.state && typeof event.state === 'object'
          ? event.state
          : (history.state && typeof history.state === 'object' ? history.state : null)
        const currentSceneOverlayKey = currentState?.am2SceneOverlay === true
          ? String(currentState.am2SceneOverlayKey ?? '').trim()
          : ''
        const top = String(stack[stack.length - 1] ?? '').trim()
        if (!currentSceneOverlayKey && this.state.lightboxOpen) {
          this.sceneOverlayStack = stack.filter(key => key !== 'lightbox')
          this.closeLightbox({ fromPop: true })
          return
        }
        if (currentSceneOverlayKey && currentSceneOverlayKey === top) return
        const popped = stack.pop()
        this.closeSceneOverlayFromPop(popped)
      },

      onMounted() {
        const saveId = this.resolveSceneSaveId()
        this.mountedSaveId = saveId ? String(saveId) : ''
        this.sceneLoadToken = 0
        this.sceneLoadKey = ''
        this.sceneLoadFatalKey = ''

        this.handles = [
          dispatcher.on(TOPICS.game.loaded,         (_e, gs) => { this.onGameLoaded(gs) }),
          dispatcher.on(TOPICS.game.started,        (_e, gs) => { this.onGameLoaded(gs) }),
          dispatcher.on(TOPICS.ui.dialogClose,      () => { this.clearCompanionSelected() }),
          dispatcher.on(TOPICS.game.actionStart,    (_e, d) => {
            this.clearUnreadLatestEntry()
            this.startActionStatus(d?.actionId)
          }),
          // actionUpdate: keep prose and quick HUD state live while actions resolve.
          dispatcher.on(TOPICS.game.actionUpdate,   () => {
            const gs = gameStore.state
            if (!gs) {
              this.update()
              return
            }
            this.update({
              quickAbilities: this.collectQuickAbilities(gs),
              quickExitActions: this.collectExitActions(gs),
              playerVitals: gs.player?.vitals ?? this.state.playerVitals,
            })
          }),
          // actionComplete: sync all state snapshots used by sidebars + quick lanes
          dispatcher.on(TOPICS.game.actionComplete, (_e, d) => {
            if (!this.shouldHandleActionStatusTerminal(d)) return
            this.stopActionStatus()
            const gs = gameStore.state
            if (!gs) return
            this.syncStateFromGame(gs)
            this.markLatestEntryUnread()
          }),
          dispatcher.on(TOPICS.game.actionFailed, (_e, d) => {
            if (!this.shouldHandleActionStatusTerminal(d)) return
            this.stopActionStatus()
            const message = formatAIErrorMessage(d?.error)
            if (/AI provider|AI configuration|provider|quota|not available|not registered|not allowed/i.test(message)) {
              import('../../app-states.js').then(({ sysNotify }) => {
                sysNotify(message)
              })
              dispatcher.trigger(this, TOPICS.ui.dialogOpen, { content: 'settings-dialog', data: { initialTab: 'advanced' } })
            }
          }),
          // inventoryChanged: room items update immediately (take/drop before narrator finishes)
          dispatcher.on(TOPICS.game.inventoryChanged, () => {
            const gs = gameStore.state
            if (gs) {
              this.update({
                roomItems: gs.currentRoom?.items ?? [],
              })
            }
          }),
          dispatcher.on(TOPICS.game.imageReady,     (_e, d) => { this.onImageReady(d) }),
          dispatcher.on(TOPICS.game.vitalsChanged,  (_e, v)  => { this.update({ playerVitals: v }) }),
          dispatcher.on(TOPICS.world.ticked, () => {
            const gs = gameStore.state
            if (gs) this.update({ worldLog: this.collectWorldLog(gs) })
          }),
          dispatcher.on(TOPICS.game.roomChanged,    (_e, r)  => { this.update({
            currentRoom: r,
            roomNpcs:    r?.npcs  ?? [],
            roomItems:   r?.items ?? [],
            roomFlags:   this.formatFlags(r?.flags),
            quickExitActions: gameStore.state ? this.collectExitActions(gameStore.state) : [],
            roomDormantFlags: [...new Set((r?.dormantFlags ?? []).map(f => {
              if (typeof f === 'string') return f
              return f?.label || f?.key || 'Condition'
            }))].slice(0, 6),
            roomName:    r?.name ?? this.state.roomName,
            selectedCompanionName: '',
            locationExpanded: false,
          }) }),
          dispatcher.on(TOPICS.ui.openItem, (_origin, data) => { openItemDialog('scene', data?.name) }),
        ]

        // Swipe gestures for both sidebars
        let touchStartX = 0
        this.onTouchStart = e => { touchStartX = e.changedTouches[0].screenX }
        this.onTouchEnd   = e => {
          const dx = e.changedTouches[0].screenX - touchStartX
          if (Math.abs(dx) < 75) return
          if (dx > 0) {
            if (this.state.statsOpen) this.update({ statsOpen: false })
            else this.openHistory()
          } else {
            if (this.state.historyOpen) this.closeHistory()
            else this.update({ statsOpen: true })
          }
        }
        document.body.addEventListener('touchstart', this.onTouchStart, { passive: true })
        document.body.addEventListener('touchend',   this.onTouchEnd,   { passive: true })
        this.syncHistoryDrawerState(this.state.historyOpen)
        this.sceneOverlayStack = []
        this.ignoreSceneOverlayPop = false
        this.onSceneOverlayPop = event => this.handleSceneOverlayPopState(event)
        window.addEventListener('popstate', this.onSceneOverlayPop)
        this.authSceneLoadUnsub = authStore.subscribe(() => this.ensureSceneLoaded())

        // Auto-save every 60 seconds
        this.autoSave = setInterval(() => {
          if (gameStore.state && authStore.user) gameStore.save()
        }, 60_000)

        this.ensureSceneLoaded()
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
        clearInterval(this.autoSave)
        clearInterval(this.actionStatusTicker)
        clearTimeout(this.arrivalDialogTimer)
        clearTimeout(this.sceneLoadRetryTimer)
        if (this.companionSelectedTimer) clearTimeout(this.companionSelectedTimer)
        document.body.removeEventListener('touchstart', this.onTouchStart)
        document.body.removeEventListener('touchend',   this.onTouchEnd)
        if (this.onSceneOverlayPop) window.removeEventListener('popstate', this.onSceneOverlayPop)
        this.authSceneLoadUnsub?.()
        this.sceneOverlayStack = []
        this.ignoreSceneOverlayPop = false
        this.syncHistoryDrawerState(false)
      },

      onGameLoaded(gs) {
        this.update({ sceneBootStatus: 'ready', sceneBootError: '' })
        this.syncStateFromGame(gs)
        this.scheduleArrivalDialog(gs)
      },

      scheduleArrivalDialog(gs, attempt = 0) {
        clearTimeout(this.arrivalDialogTimer)
        const saveId = String(this.resolveSceneSaveId() || gameStore.activeSaveId || '').trim()
        if (!saveId || this.arrivalDialogShownForSaveId === saveId) return
        const routeSaveId = typeof window !== 'undefined'
          ? /^\/play\/([^\/]+)$/.exec(String(window.location.pathname || ''))?.[1] ?? ''
          : ''
        if (routeSaveId !== saveId) {
          if (attempt >= 20) return
          this.arrivalDialogTimer = setTimeout(() => this.scheduleArrivalDialog(gs, attempt + 1), 50)
          return
        }

        this.arrivalDialogShownForSaveId = saveId

        // Defer the arrival modal slightly so route transition and input blur work settle first.
        this.arrivalDialogTimer = setTimeout(() => {
          const activeSaveId = String(this.resolveSceneSaveId() || gameStore.activeSaveId || '').trim()
          if (!activeSaveId || activeSaveId !== saveId) return

          const activeQuestEntries = (gs.journal || [])
            .filter(q => q.status === 'active')
            .map((q) => ({
              title: String(q.title || '').trim() || 'Untitled objective',
              desc: String(q.desc || '').trim() || 'No quest summary yet.',
            }))

          const clock = gs.world?.clock
          const dayStr = clock ? `Day ${clock.day}, ${String(clock.hour).padStart(2, '0')}:${String(clock.minute).padStart(2, '0')}` : ''
          const historyLen = gs.history?.length ?? 0
          const companionNames = (gs.companions ?? []).map(c => c.name).join(', ')
          const location = gs.currentRoom?.name || 'Unknown'
          const metaParts = [
            dayStr && `WORLD TIME: ${dayStr}`,
            `LOCATION: ${location}`,
            historyLen > 0 && `ACTIONS TAKEN: ${historyLen}`,
            companionNames && `COMPANIONS: ${companionNames}`,
          ].filter(Boolean)

          if (activeQuestEntries.length || metaParts.length) {
            dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, buildInfoDialog({
              data: {
                quests: activeQuestEntries,
              },
              options: {
                title: activeQuestEntries.length ? 'ACTIVE QUESTS' : 'WELCOME BACK',
                desc: activeQuestEntries.length
                  ? 'Current objectives are listed below with their latest summaries.'
                  : 'No active quests. Explore and discover new objectives.',
                meta: metaParts.join(' · '),
              },
            }))
          }
        }, 180)
      },

      onImageReady(data) {
        if (data?.kind === 'vision') {
          this.update({ visionUrl: data.url, hasVision: true })
          // Re-trigger fade-in animation on the vision button
          const visionBtn = this.root.querySelector('[data-block="vision"]')
          if (visionBtn) {
            visionBtn.classList.remove('vision-fresh')
            void visionBtn.offsetWidth
            visionBtn.classList.add('vision-fresh')
          }
        } else if (data?.kind === 'portrait') {
          this.update({ portraitUrl: data.url })
        } else if (data?.kind === 'companion-portrait') {
          // Update the portrait URL on the matching companion chip
          const companions = this.state.companions.map(c =>
            c.name === data.name ? { ...c, portraitUrl: data.url } : c
          )
          this.update({ companions })
        }
      },

      openCompanionModal(companion) {
        const vessel = (gameStore.state || {}).player
        if (!vessel) {
          dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, { content: 'chat-dialog', data: { companion }, options: { variant: 'companion' } })
          return
        }
        const actions = getCompanionActions(vessel, companion)
        const dialogData = buildCompanionDialogOptions(gameStore.state, companion, actions)
        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, dialogData)
      },

      openCompanionFromChip(companion) {
        this.markCompanionSelected(companion?.name)
        this.openCompanionModal(companion)
      },

      openPlayerModal(initialTab = 'profile') {
        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, {
          content: 'player-profile-sheet',
          data: { initialTab },
          options: {
            showFooter: false,
            showCloseButton: false,
            variant: 'profile',
            swipeDismiss: true,
          },
        })
      },

      openNotebook() {
        this.collapseSceneChrome()
        const notes = Array.isArray((gameStore.state || {}).notebook) ? gameStore.state.notebook : []
        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, NotebookModel.fromNotes(notes).toDialogPayload())
      },

      abilityHoverText(ability) {
        if (!ability) return 'Ability'
        const lines = [
          ability.name,
          ability.desc || '',
          `Cooldown: ${ability.cooldown}m${ability.onCooldown ? ` (ready in ${ability.cooldownRemaining}m)` : ''}`,
        ].filter(Boolean)
        return lines.join('\n')
      },

      effectChipText(label, dormant = false) {
        return formatEffectChipText(label, dormant)
      },

      openAbilityModal(ability) {
        if (!ability) return
        const gs = gameStore.state
        if (!gs) return
        const resolvedArgs = gameStore.getResolvedAbilityArgs(gs, ability)
        const requiresInput = resolvedArgs.length > 0
        const actions = ability.onCooldown
          ? []
          : buildAbilityModalActions(gs, ability, {
              resolvedArgs,
              targets: [gs.player.identity, ...gs.currentRoom.npcs.map(n => n.name)],
              title: ability.name,
              contextTag: 'scene_ability',
            })

        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: ability.name,
            desc: [
              ability.desc || 'No description.',
              '',
              `**COOLDOWN**\n${ability.cooldown} game-minutes`,
              '',
              requiresInput
                ? '**WHEN USED**\nYou will be asked for any needed details in a short follow-up step.'
                : '**WHEN USED**\nNo extra details required.',
            ].join('\n'),
            meta: ability.onCooldown ? `READY IN ${ability.cooldownRemaining}m` : 'READY',
            actions,
          },
        }))
      },

      useExitAction(exitAction) {
        const input = String(exitAction?.input ?? '').trim()
        if (!input) return
        gameStore.handleAction(input, undefined, {
          source: 'player',
          category: 'move',
          tool: buildToolRef('free-text', String(exitAction?.label ?? input), String(exitAction?.label ?? 'Move')),
          explicitTargets: [buildActionTarget('room', String(exitAction?.roomName ?? exitAction?.label ?? 'Room'))],
          tags: ['scene_exit_action', String(exitAction?.style ?? 'go')],
        })
      },

      onActivateKey(e, callback) {
        if (!e || (e.key !== 'Enter' && e.key !== ' ')) return
        e.preventDefault()
        callback?.()
      },

      normalizeSceneSaveIdCandidate(candidate) {
        if (typeof candidate === 'string' || typeof candidate === 'number') {
          return String(candidate).trim()
        }
        if (Array.isArray(candidate)) {
          for (const part of candidate) {
            const normalized = this.normalizeSceneSaveIdCandidate(part)
            if (normalized) return normalized
          }
          return ''
        }
        if (candidate && typeof candidate === 'object') {
          const saveIdKeys = ['saveId', 'id', 'value', 'slug']
          for (const key of saveIdKeys) {
            const normalized = this.normalizeSceneSaveIdCandidate(candidate[key])
            if (normalized) return normalized
          }
        }
        return ''
      },

      resolveSceneSaveId() {
        const routedSaveId = this.normalizeSceneSaveIdCandidate(
          Array.isArray(stateService.context) ? stateService.context[0] : stateService.context,
        )
        const routeSaveId = typeof window !== 'undefined'
          ? /^\/play\/([^\/]+)$/.exec(String(window.location.pathname || ''))?.[1] ?? ''
          : ''
        return String(
          routeSaveId
          || this.mountedSaveId
          || routedSaveId
          || gameStore.activeSaveId
          || ''
        ).trim()
      },

      isNarrowHistoryDrawer() {
        if (typeof window === 'undefined') return false
        return Number(window.innerWidth || 0) < 900
      },

      switchTab(tab) {
        this.update({ activeTab: tab })
        requestAnimationFrame(() => {
          this.$('.panel')?.scrollTo({ top: 0, behavior: 'instant' })
        })
      },
      syncHistoryDrawerState(isOpen) {
        document.documentElement.classList.toggle('am2-history-open', !!isOpen)
      },
      openHistory() {
        this.collapseSceneChrome()
        if (this.state.historyOpen) return
        this.update({ historyOpen: true })
        this.syncHistoryDrawerState(true)
      },
      closeHistory(options = null) {
        if (!this.state.historyOpen) return
        this.update({ historyOpen: false })
        this.syncHistoryDrawerState(false)
      },
      openStats() {
        this.collapseSceneChrome()
        if (this.state.statsOpen) return
        this.update({ statsOpen: true })
      },
      closeStats(options = null) {
        if (!this.state.statsOpen) return
        this.update({ statsOpen: false })
      },

      toggleHints() {
        this.collapseSceneChrome()
        dispatcher.trigger('scene-view', TOPICS.ui.tutorialStart, null)
      },

      expandLocationCard() {
        if (this.state.locationExpanded && !this.state.hudExpanded) return
        this.update({ locationExpanded: true, hudExpanded: false })
      },

      collapseLocationCard() {
        if (!this.state.locationExpanded) return
        this.update({ locationExpanded: false })
      },

      expandHud() {
        if (this.state.hudExpanded && !this.state.locationExpanded) return
        this.update({ locationExpanded: false, hudExpanded: true })
      },

      collapseHud() {
        if (!this.state.hudExpanded) return
        this.update({ hudExpanded: false })
      },

      collapseSceneChrome() {
        this.collapseHud()
        this.collapseLocationCard()
      },

      openVitalModal(kind) {
        const vitals = this.state.playerVitals
        if (!vitals) return

        const config = {
          vitality: {
            title: 'VITALITY',
            desc: 'Your physical durability. Low vitality makes combat and hazardous actions far riskier.',
          },
          spirit: {
            title: 'SPIRIT',
            desc: 'Your mental and emotional reserve. Spirit supports social pressure, focus, and strain resistance.',
          },
          essence: {
            title: 'ESSENCE',
            desc: 'Your core life force. Essence fuels mystical effects and is costly to recover when depleted.',
          },
        }
        const c = config[kind]
        if (!c) return

        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: c.title,
            desc: c.desc,
            meta: `CURRENT: ${formatVitalValue(vitals, kind)}`,
          },
        }))
      },

      openWorldLogEntry(entry) {
        if (!entry) return
        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: `WORLD LOG · ${entry.time ?? ''}`.trim(),
            desc: entry.text ?? '',
            meta: getWorldLogTypeLabel(entry),
          },
        }))
      },

      openWorldLogModal() {
        const entries = (gameStore.state || {}).worldLog ?? []
        if (!entries.length) return

        dispatcher.trigger('scene-view', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: 'WORLD LOG',
            desc: buildWorldLogModalBody(entries),
            meta: `${entries.length} ENTRIES`,
            variant: 'worldlog',
            swipeDismiss: true,
          },
        }))
      },

      openVisionLightbox(e) {
        if (e) {
          e.preventDefault?.()
          e.stopPropagation?.()
        }
        if (!this.state.visionUrl || this.state.lightboxOpen) return
        this.update({ lightboxOpen: true, lightboxZoomed: false })
        this.pushSceneOverlayEntry('lightbox')
      },
      closeLightbox(e) {
        const fromPop = e?.fromPop === true
        if (e?.stopPropagation) e.stopPropagation()
        if (!this.state.lightboxOpen) return
        this.update({ lightboxOpen: false, lightboxZoomed: false })
        if (fromPop) return
        this.consumeSceneOverlayEntry('lightbox')
      },
      toggleLightboxZoom(e) {
        if (e) e.stopPropagation()
        this.update({ lightboxZoomed: !this.state.lightboxZoomed })
      },

      focusRoomDetails(e) {
        if (e) e.stopPropagation()
        this.update({ locationExpanded: !this.state.locationExpanded, hudExpanded: false })
        e?.currentTarget?.focus?.()
      },

      startActionStatus(actionId) {
        clearInterval(this.actionStatusTicker)
        this.actionStatusIdx = 0
        this.update({
          actionProcessing: true,
          actionProcessingText: DEFAULT_PROCESS_LINE,
          activeActionId: Number.isFinite(actionId) ? actionId : null,
        })
        this.actionStatusTicker = setInterval(() => {
          this.actionStatusIdx = (this.actionStatusIdx + 1) % PROCESS_LINES.length
          this.update({ actionProcessingText: PROCESS_LINES[this.actionStatusIdx] })
        }, 1300)
      },

      stopActionStatus() {
        clearInterval(this.actionStatusTicker)
        this.update({
          actionProcessing: false,
          actionProcessingText: DEFAULT_PROCESS_LINE,
          activeActionId: null,
        })
      },

      shouldHandleActionStatusTerminal(data) {
        const current = this.state.activeActionId
        if (current == null) return true
        const incoming = data?.actionId
        if (!Number.isFinite(incoming)) return true
        return Number(incoming) === Number(current)
      },

      async ensureSceneLoaded(attempt = 0) {
        const saveId = this.resolveSceneSaveId()
        if (!saveId) {
          if (this.hasRenderableSceneState()) {
            this.update({ sceneBootStatus: 'ready', sceneBootError: '' })
            return
          }
          this.update({
            sceneBootStatus: 'error',
            sceneBootError: 'No active save id was available for this scene route.',
          })
          return
        }
        this.mountedSaveId = saveId
        if (gameStore.state && gameStore.activeSaveId === saveId) {
          this.onGameLoaded(gameStore.state)
          return
        }
        if (authStore.status === 'loading') {
          this.update({ sceneBootStatus: 'loading', sceneBootError: '' })
          return
        }
        const loadKey = `save:${saveId}`
        // Missing saves are terminal for a given route/session until the user retries or leaves the route.
        if (this.sceneLoadFatalKey === loadKey) return
        if (attempt === 0 && this.sceneLoadKey === loadKey) return
        this.sceneLoadKey = loadKey
        const token = ++this.sceneLoadToken
        this.update({ sceneBootStatus: 'loading', sceneBootError: '' })
        try {
          await persistenceService.restoreSave(saveId)
          if (token !== this.sceneLoadToken) return
          this.sceneLoadKey = ''
          this.update({ sceneBootStatus: 'ready', sceneBootError: '' })
        } catch (e) {
          if (token !== this.sceneLoadToken) return
          const message = String(e instanceof Error ? e.message : e)
          if (/Save not found/i.test(message)) {
            if (attempt < 6) {
              this.sceneLoadKey = ''
              clearTimeout(this.sceneLoadRetryTimer)
              this.sceneLoadRetryTimer = setTimeout(() => {
                this.ensureSceneLoaded(attempt + 1)
              }, 300 * (attempt + 1))
              return
            }
            this.sceneLoadKey = ''
            this.sceneLoadFatalKey = loadKey
            this.update({
              sceneBootStatus: 'error',
              sceneBootError: 'This save no longer exists. Return to the hub or load another save.',
            })
            return
          }
          console.error(e)
          if (attempt < 3) {
            this.sceneLoadKey = ''
            clearTimeout(this.sceneLoadRetryTimer)
            this.sceneLoadRetryTimer = setTimeout(() => {
              this.ensureSceneLoaded(attempt + 1)
            }, 250 * (attempt + 1))
            return
          }
          this.sceneLoadKey = ''
          this.update({
            sceneBootStatus: 'error',
            sceneBootError: 'The save could not be restored. You can retry or return to the hub.',
          })
          sysNotify('Failed to load save.')
        }
      },

      retrySceneLoad() {
        this.sceneLoadKey = ''
        this.sceneLoadFatalKey = ''
        this.ensureSceneLoaded()
      },

      returnToHub() {
        navigateState('hub')
      },
    }
  </script>
</scene-view>
